import queryLoopLoaded from "@plaudit/query-loop.api";

import debounce from '../../lib/debouncer';
import {TextQueryLoopFormField} from "./TextQueryLoopFormField";

export class DateQueryLoopFormField extends TextQueryLoopFormField {
	public constructor(field: HTMLFieldSetElement, type: string) {
		super(field, type);

		const debouncedFieldUpdate = debounce((_?) => this.updateBackingInputValue(),
			(e?) => !(e instanceof KeyboardEvent) || e.key === 'Enter' ? 50 : 500)
		const fullEventListener = (e: Event) => {
			const target = e.target;
			if (target instanceof HTMLInputElement && target.type === "radio") {
				for (const wrappers of this.field.querySelectorAll(".plauditQueryLoopField__dateItemWrapper")) {
					wrappers.classList.toggle("checked", false);
				}
				target.closest(".plauditQueryLoopField__dateItemWrapper")?.classList.toggle("checked", true);
			}
			debouncedFieldUpdate(e);
		};

		const interactableInputsContainer = this.field.querySelector('ul');
		if (interactableInputsContainer) {
			for (const input of interactableInputsContainer.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
				input.addEventListener('input', fullEventListener);
				input.addEventListener('change', fullEventListener);
			}

			for (const input of interactableInputsContainer.querySelectorAll<HTMLInputElement>('input[data-subfield]')) {
				input.addEventListener('keyup', fullEventListener);
				input.addEventListener('keypress', e => {
					if (e.key === 'Enter') {
						e.preventDefault();
					}
					fullEventListener(e);
				});
				input.addEventListener('change', fullEventListener);
			}
		}
		this.postConstruct();
	}

	private deriveValueFromRadiosAndInputs() {
		let selectedRadio = this.field.querySelector<HTMLInputElement>('input[type="radio"]:checked');
		if (selectedRadio === null) {
			const firstRadio = this.field.querySelector<HTMLInputElement>('input[type="radio"]');
			if (firstRadio === null) {
				console.error(`Unable to find any selectable radios for ${this.getLabel()}`);
				return "";
			}
			selectedRadio = firstRadio;
			selectedRadio.checked = true;
		}

		const backendId = selectedRadio.getAttribute('data-backend-id');
		if (backendId) {
			let value = '';
			let valueWithAllDefaults = '';
			const closestLI = selectedRadio.closest('li');
			if (closestLI === null) {
				console.error(`The selected radio in ${this.getLabel()} was not wrapped in a li tag`);
				return "";
			}
			for (const input of closestLI.querySelectorAll<HTMLInputElement>('input[data-subfield]')) {
				value += ',' + (input.value || (input.getAttribute('data-value-when-empty') ?? ''));
				valueWithAllDefaults += ',' + (input.getAttribute('data-value-when-empty') ?? '');
			}
			if (value === valueWithAllDefaults) {
				value = '';
			}
			return backendId + ':' + (value.length > 0 ? value.substring(1) : value);
		}
		return selectedRadio.value;
	}

	private setBackingInputValue(value: string) {
		if (super.setValue(value)) {
			this.input.dispatchEvent(new Event('input', {bubbles: true, cancelable: true}));
			return true;
		}
		return false;
	}

	private updateBackingInputValue() {
		return this.setBackingInputValue(this.deriveValueFromRadiosAndInputs());
	}

	public setValue(value: string|string[]): boolean {
		if (Array.isArray(value)) {
			value = value.join(' ');
		}
		if (value.length === 0) { // This field can't ever have a blank value, so we can safely treat blank as "default"
			value = this.getDefaultValue();
		}
		let lastColonIndex = value.lastIndexOf(':');
		if (lastColonIndex === -1) {
			const backendId = this.field.querySelector(`input[type="radio"][value="${value}"]`)?.getAttribute('data-backend-id');
			if (backendId) {
				value = backendId + ':';
				lastColonIndex = value.length - 1;
			}
		}
		if (super.setValue(value)) {
			let radioValue, parts: string[];
			if (lastColonIndex === -1) {
				radioValue = value;
				parts = [];
			} else {
				radioValue = value.substring(0, lastColonIndex);
				parts = value.substring(lastColonIndex + 1).split(',');
			}
			for (const radio of this.field.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
				if (radio.value === radioValue) {
					radio.checked = true;
					const li = radio.closest('li');
					if (li === null) {
						console.error(`One of the radios for ${this.getLabel()} was not wrapped in a li tag`);
						continue;
					}
					let part = 0;
					for (const freeformInput of li.querySelectorAll<HTMLInputElement>('input[data-subfield]')) {
						const p = part < parts.length ? parts[part++] : '';
						freeformInput.value = (p === freeformInput.getAttribute('data-value-when-empty') ? '' : p);
					}
				} else {
					radio.checked = false;
				}
				radio.closest(".plauditQueryLoopField__dateItemWrapper")?.classList.toggle("checked", radio.checked);
			}
			return true;
		}
		return false;
	}

	public getLabeledValues() {
		const selectedRadio = this.field.querySelector<HTMLInputElement>('input[type="radio"]:checked');
		const backendId = selectedRadio?.getAttribute('backend-id');
		if (backendId) {
			const res = [];
			const li = selectedRadio!.closest('li');
			if (li === null) {
				console.error(`The selected radio in ${this.getLabel()} was not wrapped in a li tag`);
				return super.getLabeledValues();
			}
			for (const freeformInput of li.querySelectorAll<HTMLInputElement>('input[data-subfield]')) {
				res.push({
					field: this.name,
					fieldLabel: `${this.getLabel()} ${freeformInput.labels?.[0]?.ariaLabel ?? freeformInput.name}`,
					value: `${backendId}:${freeformInput.getAttribute('data-subfield')}`,
					label: freeformInput.value
				});
			}
			return res;
		}
		return super.getLabeledValues();
	}

	public remove(value: string): boolean {
		const lastColonIndex = value.lastIndexOf(':');
		if (lastColonIndex !== -1) {
			const input = this.field.querySelector(`[data-backend-id="${value.substring(0, lastColonIndex)}"]`)
				?.closest('li')?.querySelector<HTMLInputElement>(`input[data-subfield="${value.substring(lastColonIndex + 1)}"]`);
			if (input) {
				input.value = '';
				return this.updateBackingInputValue();
			}
		}
		return super.remove(value);
	}

	public isSingleValued() {
		return true;
	}
}

queryLoopLoaded.then(() => {
	plaudit.queryLoop.fieldTypes.set('date', field => new DateQueryLoopFormField(field, 'date'));
});
